import { useState, useEffect } from 'react';
import { api } from '../utils/api';

export interface AlertConfig {
  id?: string;
  alertId?: string;
  unitId: string;
  metric: 'voltage' | 'battery_capacity' | 'charge_rate' | 'water_depth' | 'water_volume' | 'volume_change';
  condition: 'lt' | 'gt';
  threshold: number;
}

export function useUnitAlerts(unitId: string | undefined, metric?: string) {
  const [alerts, setAlerts] = useState<AlertConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!unitId) return;

    const fetchAlerts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await api.get(`/api/v1/units/${unitId}/alerts`);
        
        let filteredAlerts = data || [];
        if (metric) {
          filteredAlerts = filteredAlerts.filter((a: AlertConfig) => a.metric === metric);
        }
        setAlerts(filteredAlerts);
      } catch (err: any) {
        setError(err.message || 'Failed to load alerts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlerts();
  }, [unitId, metric]);

  const addAlert = async (alert: Omit<AlertConfig, 'id'>) => {
    try {
      const newAlert = await api.post(`/api/v1/units/${unitId}/alerts`, alert);
      setAlerts([...alerts, newAlert]);
      return newAlert;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to add alert');
    }
  };

  const removeAlert = async (alertId: string) => {
    try {
      await api.delete(`/api/v1/units/${unitId}/alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.id !== alertId && a.alertId !== alertId));
    } catch (err: any) {
      throw new Error(err.message || 'Failed to delete alert');
    }
  };

  return { alerts, isLoading, error, addAlert, removeAlert };
}
